// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React, { useState, ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import AppFooter from './AppFooter'
import RouteFocusAnnouncer from './RouteFocusAnnouncer'
import { useSidebarState } from './useSidebarState'
import FeedbackWidget from '../components/FeedbackWidget'
import IdleLogoutGuard from './IdleLogoutGuard'
import { useAuth } from '../auth/AuthContext'

interface Props {
  /** Optional explicit children (used for non-routed shells in tests). */
  children?: ReactNode
  /** Current screen id surfaced to FeedbackWidget. */
  currentScreen?: string
}

/**
 * Top-level layout. Renders:
 *
 *   ┌────────┬─────────────────────────────────┐
 *   │        │  TopBar (hamburger + brand)     │
 *   │ Side-  ├─────────────────────────────────┤
 *   │ bar    │                                 │
 *   │        │   Outlet (routed page)          │
 *   │        │                                 │
 *   └────────┴─────────────────────────────────┘
 *
 * Sidebar visibility / mode is owned by useSidebarState. The shell's only
 * job is to compose the three.
 *
 * Pages no longer render their own headers — they're plain content. Width
 * is unconstrained at this level; pages can constrain via their own max-width.
 */
export default function AppShell({ children, currentScreen = 'home' }: Props) {
  const sidebar = useSidebarState()
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const { me } = useAuth()

  // D-UI-1: full-width pages. Screen containers cap themselves with
  // `maxWidth: 'var(--page-max, <orig>px)'`. We set the variable ONLY for
  // non-BioAccess sessions (ASP-bound + unbound owner/admin), so BioAccess
  // keeps every original per-page fallback and renders pixel-identically
  // (standing invariant 4). Patrick-directed 2026-07-17.
  // Fail-closed: default (me not yet loaded, or any other tenant) keeps the
  // original per-page caps. Full-bleed only once /auth/me affirms the session
  // is ASP-bound or an unbound owner/admin account. (L-051: this field is
  // dbo.pharmacies.slug from /auth/me, not url_code — verified in AuthFunctions.)
  // WAS AN ALLOWLIST, NOW A DENYLIST (2026-08-27). The original released the cap
  // for ASP and unbound admins only, so EVERY other tenant silently kept the narrow
  // per-page fallbacks — a new pharmacy got the cramped layout and nobody noticed,
  // because the sessions people tested with were on the list. The constraint being
  // protected is BioAccess (standing invariant 4: pixel-identical), so name BioAccess
  // and let everyone else through. Still fail-closed while /auth/me is in flight.
  // 2026-09-11 (Patrick, from BioAccess screenshots: "all bioaccess pharmacy screens not using the
  // full width"): the BioAccess exception is RETIRED — every loaded session is full-bleed. The only
  // remaining condition is fail-closed while /auth/me is in flight.
  const fullBleed = me != null
  const shellStyle: React.CSSProperties = fullBleed
    ? ({ ...pageStyle, ['--page-max' as never]: '100%' } as React.CSSProperties)
    : pageStyle

  // Sidebar is "open" for aria-expanded purposes whenever it's visible:
  //   - desktop: always considered open (toggle just collapses)
  //   - tablet/mobile: open=overlayOpen
  const sidebarOpen = sidebar.mode === 'desktop' ? !sidebar.collapsed : sidebar.overlayOpen

  return (
    <div style={shellStyle}>
      <a href="#main" className="skip-link">Skip to main content</a>

      <div style={mainRowStyle}>
        <div id="appshell-sidebar">
          <Sidebar
            mode={sidebar.mode}
            collapsed={sidebar.collapsed}
            overlayOpen={sidebar.overlayOpen}
            closeOverlay={sidebar.closeOverlay}
          />
        </div>

        <div style={contentColStyle}>
          <TopBar
            onToggleSidebar={sidebar.toggle}
            sidebarOpen={sidebarOpen}
            onOpenFeedback={() => setFeedbackOpen(true)}
          />

          <main id="main" tabIndex={-1} style={mainContentStyle}>
            <RouteFocusAnnouncer />
            {children ?? <Outlet />}
          </main>
          <AppFooter />
        </div>
      </div>

      <IdleLogoutGuard />
      {/* Floating IG Dosing launcher removed (2026-08-27): it sat over the
          bottom-right of every screen and covered dashboard tiles. The calculator
          is unchanged and still reachable from the nav item and
          /carerx/dosing-calculator — only the global floater is gone. */}

      {feedbackOpen && (
        <FeedbackWidget
          screen={currentScreen}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  background: 'var(--color-bg, #fafaf9)',
}

const mainRowStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
}

const contentColStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}

const mainContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  background: 'var(--color-bg, #fafaf9)',
}
